// ============================================
// src/components/meetings/MeetingLive.js
// Live Meeting Recording - Zero Cost Solution
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function MeetingLive({ meeting, userData, onClose }) {
  const [attendees, setAttendees] = useState([]);
  const [minutes, setMinutes] = useState('');
  const [actionItems, setActionItems] = useState([]);
  const [newActionItem, setNewActionItem] = useState({
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium'
  });
  const [residents, setResidents] = useState([]);
  const [presentCount, setPresentCount] = useState(0);
  const [autoSave, setAutoSave] = useState(false);
  const [availableResidents, setAvailableResidents] = useState([]);

  useEffect(() => {
  fetchResidents();
  loadExistingData();
}, [meeting]);

  // Auto-save minutes every 30 seconds
  useEffect(() => {
    if (autoSave && minutes) {
      const timer = setInterval(() => {
        saveMinutes();
      }, 30000);
      return () => clearInterval(timer);
    }
  }, [autoSave, minutes]);

  const fetchResidents = async () => {
  const { data } = await supabase
    .from('residents')
    .select('id, name, flat_number, role') // Add role to the select
    .eq('account_status', 'active')
    .order('flat_number');
  
  if (data) {
    setResidents(data);
    // Filter out superadmin for the dropdown but keep for attendance
    const dropdownResidents = data.filter(r => r.role !== 'superadmin');
    setAvailableResidents(dropdownResidents);
    
    // Initialize attendance with all residents (including superadmin)
    const attendanceList = data.map(resident => ({
      ...resident,
      is_present: false
    }));
    setAttendees(attendanceList);
  }
};

  const loadExistingData = async () => {
    // Load existing minutes
    if (meeting.minutes) {
      setMinutes(meeting.minutes);
    }

    // Load existing attendance
    const { data: attendanceData } = await supabase
      .from('meeting_attendance')
      .select('*')
      .eq('meeting_id', meeting.id);

    if (attendanceData) {
      setAttendees(prev => prev.map(attendee => {
        const record = attendanceData.find(a => a.resident_id === attendee.id);
        return { ...attendee, is_present: record ? record.is_present : false };
      }));
      setPresentCount(attendanceData.filter(a => a.is_present).length);
    }

    // Load action items
    const { data: actionData } = await supabase
      .from('action_items')
      .select('*')
      .eq('meeting_id', meeting.id)
      .order('created_at');

    if (actionData) {
      setActionItems(actionData);
    }
  };

  const toggleAttendance = async (resident) => {
    const newStatus = !resident.is_present;
    
    // Update local state
    setAttendees(prev => prev.map(a => 
      a.id === resident.id ? { ...a, is_present: newStatus } : a
    ));

    // Update database
    const { data: existing } = await supabase
      .from('meeting_attendance')
      .select('id')
      .eq('meeting_id', meeting.id)
      .eq('resident_id', resident.id)
      .single();

    if (existing) {
      await supabase
        .from('meeting_attendance')
        .update({ is_present: newStatus, marked_at: new Date() })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('meeting_attendance')
        .insert([{
          meeting_id: meeting.id,
          resident_id: resident.id,
          is_present: newStatus,
          marked_at: new Date()
        }]);
    }

    setPresentCount(prev => newStatus ? prev + 1 : prev - 1);
  };

  const saveMinutes = async () => {
    try {
      await supabase
        .from('meetings')
        .update({ minutes, status: 'in_progress' })
        .eq('id', meeting.id);
      
      // Show save indicator
      const indicator = document.getElementById('save-indicator');
      if (indicator) {
        indicator.style.display = 'block';
        setTimeout(() => {
          indicator.style.display = 'none';
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving minutes:', error);
    }
  };

  const addActionItem = async () => {
  if (!newActionItem.description) return;

  try {
    // Get resident details if individual assignment
    let assignedToName = 'All';
    let assignedToId = null;
    
    if (newActionItem.assigned_to && newActionItem.assigned_to !== 'all') {
      const assignedResident = availableResidents.find(r => r.id === newActionItem.assigned_to);
      if (assignedResident) {
        assignedToName = `${assignedResident.flat_number} - ${assignedResident.name}`;
        assignedToId = assignedResident.id;
      }
    }

    const { data, error } = await supabase
      .from('action_items')
      .insert([{
        meeting_id: meeting.id,
        description: newActionItem.description,
        assigned_to: assignedToName, // Store name for display
        assigned_to_id: assignedToId, // Store ID for queries
        due_date: newActionItem.due_date || null,
        priority: newActionItem.priority,
        status: 'pending',
        created_at: new Date().toISOString()
      }])
      .select();

    if (!error && data) {
      setActionItems([...actionItems, data[0]]);
      setNewActionItem({
        description: '',
        assigned_to: '',
        due_date: '',
        priority: 'medium'
      });
    }
  } catch (error) {
    console.error('Error adding action item:', error);
  }
};

  const generateWhatsAppMessage = () => {
    const presentMembers = attendees.filter(a => a.is_present);
    
    let message = `📅 *${meeting.title}*\n`;
    message += `📍 ${meeting.venue}\n`;
    message += `📆 ${new Date(meeting.meeting_date).toLocaleDateString()}\n`;
    message += `👥 Attendance: ${presentCount}/${attendees.length}\n\n`;
    
    message += `📝 *MINUTES OF MEETING*\n`;
    message += `${minutes || 'No minutes recorded'}\n\n`;
    
    if (actionItems.length > 0) {
      message += `✅ *ACTION ITEMS*\n`;
      actionItems.forEach((item, index) => {
        message += `\n${index + 1}. ${item.description}\n`;
        message += `   👤 Assigned: ${item.assigned_to || 'All'}\n`;
        if (item.due_date) {
          message += `   📅 Due: ${new Date(item.due_date).toLocaleDateString()}\n`;
        }
        message += `   🔴 Priority: ${item.priority.toUpperCase()}\n`;
      });
    }
    
    message += `\n---\n_Generated from Society Management System_`;
    
    return message;
  };

  const copyToWhatsApp = () => {
    const message = generateWhatsAppMessage();
    navigator.clipboard.writeText(message).then(() => {
      alert('Meeting summary copied! Paste it in your WhatsApp group.');
    }).catch(err => {
      console.error('Failed to copy:', err);
      // Fallback - show message in textarea
      const textarea = document.createElement('textarea');
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Meeting summary copied! Paste it in your WhatsApp group.');
    });
  };

  const finishMeeting = async () => {
    await saveMinutes();
    await supabase
      .from('meetings')
      .update({ status: 'completed' })
      .eq('id', meeting.id);
    
    copyToWhatsApp();
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'white',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        padding: '20px',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>🔴 LIVE: {meeting.title}</h2>
            <p style={{ margin: '5px 0', opacity: 0.9 }}>
              {new Date(meeting.meeting_date).toLocaleDateString()} | {meeting.venue}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={copyToWhatsApp}
              style={{
                padding: '10px 20px',
                background: '#25D366',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              📱 Copy for WhatsApp
            </button>
            <button
              onClick={finishMeeting}
              style={{
                padding: '10px 20px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              End Meeting
            </button>
          </div>
        </div>
      </div>
      
      <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
        {/* Left Column - Attendance */}
        <div>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              👥 Attendance ({presentCount}/{attendees.length})
            </h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {attendees.map(resident => (
                <div
                  key={resident.id}
                  onClick={() => toggleAttendance(resident)}
                  style={{
                    padding: '10px',
                    marginBottom: '5px',
                    background: resident.is_present ? '#d4edda' : '#f8f9fa',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>
                    {resident.flat_number} - {resident.name}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: resident.is_present ? '#28a745' : '#dc3545',
                    color: 'white',
                    fontSize: '12px'
                  }}>
                    {resident.is_present ? 'Present' : 'Absent'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Minutes & Action Items */}
        <div>
          {/* Minutes */}
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>📝 Meeting Minutes</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="checkbox"
                    checked={autoSave}
                    onChange={(e) => setAutoSave(e.target.checked)}
                  />
                  Auto-save
                </label>
                <span id="save-indicator" style={{
                  display: 'none',
                  padding: '5px 10px',
                  background: '#28a745',
                  color: 'white',
                  borderRadius: '5px',
                  fontSize: '12px'
                }}>
                  Saved ✓
                </span>
              </div>
            </div>
            <textarea
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="Type meeting minutes here..."
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '5px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}
            />
            <button
              onClick={saveMinutes}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                background: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Save Minutes
            </button>
          </div>

          {/* Action Items */}
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>✅ Action Items</h3>
            
            {/* Add New Action Item */}
            <div style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '20px'
                }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                <input
                type="text"
                placeholder="Action item description..."
                value={newActionItem.description}
                onChange={(e) => setNewActionItem({...newActionItem, description: e.target.value})}
                style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '5px'
                }}
                />
                <select
                    value={newActionItem.assigned_to}
                    onChange={(e) => setNewActionItem({...newActionItem, assigned_to: e.target.value})}
                    style={{
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        background: 'white'
                    }}
                    >
                    <option value="">Assign to...</option>
                    <option value="all">All Residents</option>
                    <optgroup label="Individual Residents">
                        {availableResidents
                        .filter(resident => resident.role !== 'Super Admin') // Exclude superadmin
                        .map(resident => (
                            <option key={resident.id} value={resident.id}>
                            {resident.flat_number} - {resident.name}
                            </option>
                        ))}
                    </optgroup>
                    </select>
                        
    <input
      type="date"
      value={newActionItem.due_date}
      onChange={(e) => setNewActionItem({...newActionItem, due_date: e.target.value})}
      style={{
        padding: '8px',
        border: '1px solid #ddd',
        borderRadius: '5px'
      }}
    />
  </div>
  
  <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
        <select
        value={newActionItem.priority}
        onChange={(e) => setNewActionItem({...newActionItem, priority: e.target.value})}
        style={{
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '5px'
        }}
        >
        <option value="low">Low Priority</option>
        <option value="medium">Medium Priority</option>
        <option value="high">High Priority</option>
        </select>
        
        <button
        onClick={addActionItem}
        style={{
            padding: '8px 20px',
            background: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
        }}
        >
        Add Action Item
        </button>
    </div>
    </div>

            {/* Action Items List */}
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {actionItems.map((item, index) => (
                <div key={item.id} style={{
                  padding: '10px',
                  marginBottom: '10px',
                  background: '#f8f9fa',
                  borderRadius: '5px',
                  borderLeft: `4px solid ${
                    item.priority === 'high' ? '#dc3545' :
                    item.priority === 'medium' ? '#ffc107' : '#28a745'
                  }`
                }}>
                  <div style={{ fontWeight: '600' }}>
                    {index + 1}. {item.description}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Assigned: {item.assigned_to || 'All'} | 
                    {item.due_date && ` Due: ${new Date(item.due_date).toLocaleDateString()} |`}
                    Priority: {item.priority.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MeetingLive;